/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Inject, Injectable } from '@angular/core';
import {
  AbstractFilterOption,
  FilterValue,
  containsIgnoreCase,
  isAndFilterOption,
  isMultipleFilterOption,
  isOrFilterOption,
  isSingleFilterOption,
} from '@elio/emanager-front/shared/models';
import { applyFilters } from '@elio/emanager-front/shared/utils';
import { SortDirection } from '@elio/shared/models';
import {
  sessionStorageGet,
  sessionStorageListKeys,
  sessionStorageRemove,
  sessionStorageSave,
  sortArrayByProperty,
} from '@elio/shared/utils';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, debounceTime, distinctUntilChanged, merge, pipe, takeUntil, tap } from 'rxjs';
import { FILTERS_PERSIST_ID, FilterableStore, FilterableType } from './filter.token';

type SortFunction<T, SortBy = keyof T> = (items: T[], property: SortBy, sortDirection: SortDirection) => T[];

export interface FilterState<T, SortBy = keyof T> {
  items: T[];
  searchValue: string; // search field value
  searchProperties: Array<keyof T>; // properties on which the search applies
  searchFilters?: AbstractFilterOption<T>; // search filter list
  filtersTree?: AbstractFilterOption<T>;
  activeFiltersIds: string[];
  sortDirection: SortDirection;
  sortProperty: SortBy;
  sortFunction: SortFunction<T, SortBy>;
}

const ACTIVE_FILTERS_PERSIST_ID_PREFIX = 'activefilters-';
const SORT_PERSIST_ID_PREFIX = 'sort-';

@Injectable()
export class FilterStore<T extends FilterableType, SortBy = keyof T>
  extends ComponentStore<FilterState<T, SortBy>>
  implements FilterableStore<T>
{
  private readonly DEFAULT_SORT_OPTIONS = { sortDirection: 1, sortPropery: 'id' as SortBy };

  private readonly activeFiltersPersistId: string;
  private readonly sortPersistId: string;

  constructor(@Inject(FILTERS_PERSIST_ID) persistId: string) {
    super({
      items: [],
      searchValue: '',
      searchProperties: [],
      searchFilters: undefined,
      filtersTree: undefined,
      activeFiltersIds: [],
      sortDirection: 1,
      sortProperty: 'id' as unknown as SortBy,
      sortFunction: sortArrayByProperty as unknown as SortFunction<T, SortBy>,
    });
    this.activeFiltersPersistId = ACTIVE_FILTERS_PERSIST_ID_PREFIX + persistId;
    this.sortPersistId = SORT_PERSIST_ID_PREFIX + persistId;
  }

  readonly searchFilters$ = this.select((state) => state.searchFilters);
  readonly filtersTree$ = this.select((state) => state.filtersTree);
  readonly activeFiltersIds$ = this.select((state) => state.activeFiltersIds);
  readonly activeFiltersSize$ = this.select((state) => state.activeFiltersIds.length);
  readonly items$ = this.select((state) => state.items);
  readonly searchValue$ = this.select((state) => state.searchValue);
  readonly searchProperties$ = this.select((state) => state.searchProperties);
  readonly sortProperty$ = this.select((state) => state.sortProperty);
  readonly sortDirection$ = this.select((state) => state.sortDirection);
  readonly sortFunction$ = this.select((state) => state.sortFunction);

  private readonly sortedItems$ = this.select(
    this.items$,
    this.sortDirection$,
    this.sortFunction$,
    this.sortProperty$,
    (items, sortDirection, sortFunction, sortProperty) => sortFunction(items, sortProperty, sortDirection),
    { debounce: true }
  );

  readonly filteredItems$ = this.select(
    this.sortedItems$,
    this.filtersTree$,
    this.activeFiltersIds$,
    this.searchValue$,
    this.searchProperties$,
    (items, filtersTree, activeFiltersIds, searchValue, searchProperties) =>
      this.filterItems(items, filtersTree, activeFiltersIds, searchValue, searchProperties),
    { debounce: true }
  );

  readonly setItems = this.updater(
    (state, items: T[]): FilterState<T, SortBy> => ({
      ...state,
      items: [...items],
    })
  );

  readonly setupSort = this.updater<{ sortProperty?: SortBy; sortDirection?: SortDirection }>(
    (state, sortParams): FilterState<T, SortBy> => {
      const sortOptions = sessionStorageGet(this.sortPersistId) ?? {
        ...this.DEFAULT_SORT_OPTIONS,
        ...sortParams,
      };
      return {
        ...state,
        ...sortOptions,
      };
    }
  );

  readonly updateSort = this.updater<{ sortProperty?: SortBy; sortDirection?: SortDirection }>(
    (state, sortParams): FilterState<T, SortBy> => {
      const sortOptions = {
        sortProperty: sortParams.sortProperty ?? state.sortProperty,
        sortDirection: sortParams.sortDirection ?? state.sortDirection,
      };
      sessionStorageSave(this.sortPersistId, sortOptions);
      return {
        ...state,
        ...sortOptions,
      };
    }
  );

  readonly setSortFunction = this.updater(
    (state, sortFunction: SortFunction<T, SortBy>): FilterState<T, SortBy> => ({
      ...state,
      sortFunction,
    })
  );

  setUpFilters({
    filters,
    searchValue,
    searchProperties,
  }: {
    filters?: Observable<AbstractFilterOption<T>> | AbstractFilterOption<T>;
    defaultActiveFiltersIds?: Observable<string[]>;
    searchValue?: Observable<string>;
    searchProperties?: (keyof T)[];
  }) {
    if (filters) {
      this.setFiltersTree(filters);
      this.initActiveFilters(filters);
    }
    if (searchValue) {
      this.setUpSearch(searchValue);
    }
    if (searchProperties) {
      this.setSearchProperties(searchProperties);
    }

    merge(this.activeFiltersIds$, this.items$)
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.processCounter());
  }

  private readonly setUpSearch = this.effect<string>(
    pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap((text) => this.setSearchValue(text))
    )
  );

  private readonly setSearchValue = this.updater(
    (state, searchValue: string): FilterState<T, SortBy> => ({
      ...state,
      searchValue,
    })
  );

  private readonly setSearchProperties = this.updater(
    (state, searchProperties: (keyof T)[]): FilterState<T, SortBy> => ({
      ...state,
      searchProperties,
    })
  );

  private readonly setFiltersTree = this.updater(
    (state, filtersTree: AbstractFilterOption<T>): FilterState<T, SortBy> => ({
      ...state,
      filtersTree,
    })
  );

  private readonly initActiveFilters = this.updater(
    (state, filtersTree: AbstractFilterOption<T>): FilterState<T, SortBy> => {
      const defaultActiveFilter = this.buildDefaultActiveFilters(filtersTree);
      const sessionActiveFilters = sessionStorageGet<string[] | null>(this.activeFiltersPersistId) ?? undefined;
      return {
        ...state,
        activeFiltersIds: sessionActiveFilters ?? defaultActiveFilter,
      };
    }
  );

  readonly resetfilter = this.updater((state): FilterState<T, SortBy> => {
    sessionStorageRemove(this.activeFiltersPersistId);
    return {
      ...state,
      activeFiltersIds: state.filtersTree ? this.buildDefaultActiveFilters(state.filtersTree) : [],
    };
  });

  private buildDefaultActiveFilters = (filter: AbstractFilterOption<T>): string[] => {
    if (isSingleFilterOption(filter)) {
      if (filter.isDefault) {
        return [filter.filterValue.id];
      }
      return [];
    }
    if (isMultipleFilterOption(filter)) {
      return Array.prototype.concat.apply([], filter.subFilters.map(this.buildDefaultActiveFilters));
    }
    return [];
  };

  readonly enableFilter = this.updater((state, filterValue: FilterValue<T>): FilterState<T, SortBy> => {
    let newActiveFiltersIds: string[];
    if (state.activeFiltersIds.find((activeFilterId) => activeFilterId === filterValue.id)) {
      newActiveFiltersIds = [...state.activeFiltersIds];
    } else {
      newActiveFiltersIds = [...state.activeFiltersIds, filterValue.id];
    }
    sessionStorageSave(this.activeFiltersPersistId, newActiveFiltersIds);
    return {
      ...state,
      activeFiltersIds: newActiveFiltersIds,
    };
  });

  readonly disableFilter = this.updater((state, filterValue: FilterValue<T>): FilterState<T, SortBy> => {
    const newActiveFiltersIds = state.activeFiltersIds.filter((activeFilterId) => activeFilterId !== filterValue.id);
    sessionStorageSave(this.activeFiltersPersistId, newActiveFiltersIds);
    return {
      ...state,
      activeFiltersIds: newActiveFiltersIds,
    };
  });

  public processCounter = this.updater((state): FilterState<T, SortBy> => {
    return {
      ...state,
      filtersTree: state.filtersTree
        ? this.addCount(state.filtersTree, state.items, state.activeFiltersIds, {
            rootFilter: state.filtersTree,
            searchValue: state.searchValue,
            searchProperties: state.searchProperties,
          })
        : undefined,
    };
  });

  readonly cleanupOnLogout = this.effect<void>(
    pipe(
      tap(() => {
        const keysToRemove = sessionStorageListKeys().filter(
          (key) => key.startsWith(ACTIVE_FILTERS_PERSIST_ID_PREFIX) || key.startsWith(SORT_PERSIST_ID_PREFIX)
        );
        for (const key of keysToRemove) {
          sessionStorageRemove(key);
        }
      })
    )
  );

  /**
   * Add count to filters.
   */
  private addCount = <F extends AbstractFilterOption<T>>(
    filter: F,
    items: T[],
    activeFiltersIds: string[],
    params: { rootFilter: AbstractFilterOption<T>; searchValue: string | undefined; searchProperties: (keyof T)[] }
  ): F => {
    let result: F;
    if (isSingleFilterOption(filter)) {
      const simulatedActiveFiltersIds = activeFiltersIds.includes(filter.filterValue.id)
        ? [...activeFiltersIds]
        : [...activeFiltersIds, filter.filterValue.id];
      result = {
        ...filter,
        count: this.filterItems(
          items,
          params.rootFilter,
          simulatedActiveFiltersIds,
          params.searchValue,
          params.searchProperties
        ).length,
      };
    } else if (isOrFilterOption(filter)) {
      // we need to count only for current subFilter, so sibling filters have to be disabled
      const subFiltersIds = filter.subFilters.map((subFilter) => subFilter.id);
      const simulatedActiveFilters = activeFiltersIds.filter((filterId) => !subFiltersIds.includes(filterId));
      result = {
        ...filter,
        subFilters: filter.subFilters.map((subFilter) =>
          this.addCount(subFilter, items, simulatedActiveFilters, params)
        ),
      };
    } else if (isAndFilterOption(filter)) {
      result = {
        ...filter,
        subFilters: filter.subFilters.map((subFilter) => this.addCount(subFilter, items, activeFiltersIds, params)),
      };
    } else {
      throw new Error('Unknown filter type ' + filter);
    }
    return result;
  };

  /** Filter items with both search value and active filters */
  private filterItems(
    items: T[],
    filters: AbstractFilterOption<T> | undefined,
    activeFilterIds: string[],
    searchValue: string | undefined,
    searchProperties: (keyof T)[]
  ): T[] {
    let filteredItems = [...items];

    // filter items with active filters
    if (filters) {
      filteredItems = applyFilters(filteredItems, filters, activeFilterIds);
    }

    // filter items with search value
    if (searchValue && searchProperties.length > 0) {
      filteredItems = filteredItems.filter((item) =>
        searchProperties.reduce(
          (keepResult: boolean, property) => keepResult || containsIgnoreCase(item[property], searchValue),
          false
        )
      );
    }

    return filteredItems;
  }
}
